import React, { useState, useEffect } from 'react';
import classNames from 'classnames';
import { NumberInput } from '../../../Controls/Input/NumberInput';
import { useFilterField } from '../../hooks/useFilterField';
import { useFilterBarContext } from '../../state/FilterBarContext';
import type { NumericRangeFilterConfig, NumericRangeValue } from '../../types';

import cn from './styles.module.scss';

export interface NumericRangeFilterControlProps {
  name: string;
  /** When set, the Min/Max placeholders are formatted as `${label} min` / `${label} max`. */
  placeholder?: string;
}

/**
 * New wrapper composing two NumberInput primitives.
 *
 * Plan Section "Recommended Approach":
 *   "NumericRangeFilterControl — two NumberInput instances with cross-validation;
 *    currency/percent formatting layer lives here."
 *
 * Currency and percent formatting are visual prefixes/suffixes — the stored
 * value is always plain numeric tuple.
 */
export const NumericRangeFilterControl: React.FC<NumericRangeFilterControlProps> = ({ name, placeholder: placeholderOverride }) => {
  const { value, setValue, config } = useFilterField(name);
  const { size } = useFilterBarContext();

  // Local state to allow editing one bound without committing — debounced commit
  // on blur or after value changes.
  const initialPair = (value as NumericRangeValue) ?? [null, null];
  const [minStr, setMinStr] = useState<string>(toStr(initialPair?.[0]));
  const [maxStr, setMaxStr] = useState<string>(toStr(initialPair?.[1]));

  useEffect(() => {
    const pair = (value as NumericRangeValue) ?? [null, null];
    setMinStr(toStr(pair?.[0]));
    setMaxStr(toStr(pair?.[1]));
  }, [value]);

  if (!config || config.type !== 'NUMERIC_RANGE') return null;
  const nr = config as NumericRangeFilterConfig;
  const size_ = nr.size ?? size;
  const format = nr.format ?? 'number';

  const commitValue = (rawMin: string, rawMax: string) => {
    const min = rawMin === '' ? null : Number(rawMin);
    const max = rawMax === '' ? null : Number(rawMax);
    if (min === null && max === null) {
      setValue(null);
      return;
    }
    // Cross-validation: if min > max, swap.
    if (min !== null && max !== null && min > max) {
      setValue([max, min]);
      return;
    }
    setValue([min, max]);
  };

  const prefix = format === 'currency' ? (nr.currency ?? '$') : undefined;
  const suffix = format === 'percent' ? '%' : undefined;
  const minPlaceholder = placeholderOverride ? `${placeholderOverride} min` : 'Min';
  const maxPlaceholder = placeholderOverride ? `${placeholderOverride} max` : 'Max';

  return (
    <div className={classNames(cn.numericRange)} {...(nr.controlProps && { 'data-testid': nr.controlProps['data-testid'] })}>
      <NumberInput
        {...nr.controlProps?.minProps}
        value={minStr}
        onChange={(v) => setMinStr(v)}
        onBlur={() => commitValue(minStr, maxStr)}
        size={size_}
        placeholder={minPlaceholder}
        prefix={prefix}
        suffix={suffix}
      />
      <span className={cn.numericRangeDash}>—</span>
      <NumberInput
        {...nr.controlProps?.maxProps}
        value={maxStr}
        onChange={(v) => setMaxStr(v)}
        onBlur={() => commitValue(minStr, maxStr)}
        size={size_}
        placeholder={maxPlaceholder}
        prefix={prefix}
        suffix={suffix}
      />
    </div>
  );
};

function toStr(n: number | null | undefined): string {
  if (n === null || n === undefined || Number.isNaN(n)) return '';
  return String(n);
}
